import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';
import leaoImage from '../../images/leão_abertura.png';
import coalaImage from '../../images/coala_ioga.png';

const PanicButtonStep1 = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Detectar dark mode
    const checkDarkMode = () => {
      const isDark = document.body.classList.contains('dark-mode');
      setIsDarkMode(isDark);
    };

    // Detectar mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Verificar inicialmente
    checkDarkMode();
    checkMobile();

    // Observer para mudanças na classe do body
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listener para mudanças de tamanho da tela
    const handleResize = () => checkMobile();
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleSelect = (choice) => {
    if (isMobile) {
      setSelectedOption(choice);
    } else {
      sessionStorage.setItem('panicArchetype', choice);
      navigate('/panic-button/result');
    }
  };

  const handleProceed = () => {
    if (selectedOption) {
      sessionStorage.setItem('panicArchetype', selectedOption);
      navigate('/panic-button/result');
    }
  };

  const containerStyles = {
    minHeight: '100vh',
    padding: '20px',
    background: '#000000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    color: isDarkMode ? '#ffffff' : '#ffffff',
    position: 'relative'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '3rem',
    maxWidth: '800px'
  };

  const titleStyles = {
    fontSize: isMobile ? '2rem' : '2.5rem',
    fontWeight: '700',
    marginBottom: '1rem',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text'
  };

  const subtitleStyles = {
    fontSize: isMobile ? '1rem' : '1.2rem',
    opacity: 0.8,
    lineHeight: '1.6',
    color: '#cccccc'
  };

  const introTextStyles = {
    fontSize: isMobile ? '1rem' : '1.1rem',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 3rem',
    lineHeight: '1.7',
    opacity: 0.9,
    color: '#e0e0e0'
  };

  const optionsContainerStyles = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: '2rem',
    maxWidth: '1000px',
    width: '100%',
    margin: '0 auto'
  };

  const optionCardStyles = (isSelected) => ({
    flex: 1,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '2rem',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: isSelected ? '3px solid #4ecdc4' : '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: isSelected 
      ? '0 0 30px rgba(78, 205, 196, 0.4)' 
      : '0 10px 40px rgba(0, 0, 0, 0.1)',
    transform: isSelected ? 'translateY(-5px)' : 'translateY(0)',
    textAlign: 'center'
  });

  const imageStyles = {
    width: '100%',
    maxWidth: '200px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '15px',
    marginBottom: '1.5rem',
    backgroundColor: '#f0f0f0'
  };

  const captionStyles = {
    fontSize: '1.3rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: '#4ecdc4'
  };

  const descriptionStyles = {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    opacity: 1,
    color: isDarkMode ? '#d0d0d0' : '#000000'
  };

  const buttonStyles = {
    background: selectedOption 
      ? (isDarkMode ? '#4ecdc4' : '#667eea') 
      : (isDarkMode ? '#555555' : '#cccccc'),
    color: selectedOption ? '#ffffff' : (isDarkMode ? '#aaaaaa' : '#666666'),
    border: 'none',
    padding: '15px 30px',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: selectedOption ? 'pointer' : 'not-allowed',
    transition: 'all 0.3s',
    opacity: selectedOption ? 1 : 0.6,
    marginTop: '2rem'
  };

  return (
    <div style={containerStyles}>
      <ThemeToggle />
      
      <div style={headerStyles}>
        <h1 style={titleStyles}>Botão do Pânico</h1>
        <p style={subtitleStyles}>Escolha seu arquétipo de controle da ansiedade</p>
      </div>

      <p style={introTextStyles}>
        Identifique qual arquétipo melhor representa seu estado atual e descubra 
        as técnicas mais adequadas para você:
      </p>

      <div style={optionsContainerStyles}>
        <div 
          style={optionCardStyles(isMobile && selectedOption === 'leao')}
          onClick={() => handleSelect('leao')}
        >
          <img
            src={leaoImage}
            alt="Leão - Força e enfrentamento"
            style={imageStyles}
          />
          <div style={captionStyles}>🦁 Leão</div>
          <div style={descriptionStyles}>
            <strong>Estou em crise intensa e preciso de técnicas imediatas</strong><br/>
            Técnicas fortes e de emergência para crises ansiosas agudas. 
            Representa força, coragem e enfrentamento direto da ansiedade.
          </div>
        </div>

        <div 
          style={optionCardStyles(isMobile && selectedOption === 'coala')}
          onClick={() => handleSelect('coala')}
        >
          <img
            src={coalaImage}
            alt="Coala - Calma e equilíbrio"
            style={imageStyles}
          />
          <div style={captionStyles}>🐨 Coala</div>
          <div style={descriptionStyles}>
            <strong>Quero controlar melhor minha ansiedade no dia a dia</strong><br/>
            Técnicas suaves e sustentáveis para concentração e ansiedade leve. 
            Representa calma, equilíbrio e desenvolvimento contínuo.
          </div>
        </div>
      </div>

      {isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={handleProceed}
            disabled={!selectedOption}
            style={buttonStyles}
          >
            {selectedOption ? 'Prosseguir' : 'Selecione uma opção'}
          </button>
        </div>
      )}

      {/* Indicador de progresso */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginTop: '3rem',
        alignItems: 'center'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: '#4ecdc4'
        }} />
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.3)'
        }} />
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.3)'
        }} />
      </div>

      <div style={{
        position: 'absolute',
        bottom: '20px',
        fontSize: '0.9rem',
        opacity: 0.6,
        color: '#cccccc'
      }}>
        Escolha seu arquétipo
      </div>
    </div>
  );
};

export default PanicButtonStep1;